/**
 * Attention Inbox v2 — pure engine (GET /workspace + derive closure).
 * Категория = что сделать; приоритет P0–P3 = насколько срочно.
 */
import dayjs from 'dayjs';
import { TASK_REGISTRY } from '../constants/taskRegistry';
import type { WorkspaceApplication, WorkspaceRoute, WorkspaceTask } from '../store/api/workspaceApi';
import {
  isOpenWorkspaceTask,
  requiresClosureProjection,
  workspaceTaskIsOverdue,
} from './dashboardTaskKpiSelectors';
import { deriveReactiveWorkflowProblems } from './workflowDerivedProblems';
import { getWorkspaceTaskSemantics, isFinanceWorkspaceTask, resolveInboxCategory } from './workspaceTaskSemantics';

export const WORKSPACE_ATTENTION_INBOX_ENABLED = true;
/** @deprecated use WORKSPACE_ATTENTION_INBOX_ENABLED */
export const WORKSPACE_ATTENTION_INBOX_V1_ENABLED = WORKSPACE_ATTENTION_INBOX_ENABLED;

export const WORKSPACE_ATTENTION_INBOX_STORAGE_KEY = 'workspace_attention_inbox_v2';

export const ATTENTION_INBOX_CATEGORY_LIMIT = 5;
export const HYBRID_GROUP_THRESHOLD = 3;

/**
 * Viewport-adaptive max height for expanded inbox body (not fixed px).
 * Tuned for 1920×1080: inbox + KPI + table headers without page scroll.
 */
export const ATTENTION_INBOX_EXPANDED_MAX_HEIGHT = 'min(42vh, calc(100vh - 22rem))';

export type AttentionInboxCategory = 'documents' | 'finance' | 'operations' | 'closure';

export type AttentionPriority = 'P0' | 'P1' | 'P2' | 'P3';

export type AttentionEntityRef = { kind: 'route' | 'application'; id: number };

export type AttentionSignal = {
  id: string;
  category: AttentionInboxCategory;
  priority: AttentionPriority;
  title: string;
  entityLabel: string;
  subtitle?: string;
  entity: AttentionEntityRef;
  task: WorkspaceTask | null;
  dueDate?: string | null;
  overdueDays?: number;
  source: 'task' | 'closure';
};

export type AttentionDisplaySignal = {
  kind: 'signal';
  signal: AttentionSignal;
  closureBlocked?: boolean;
};

export type AttentionDisplayGroup = {
  kind: 'group';
  id: string;
  entity: AttentionEntityRef;
  entityLabel: string;
  category: AttentionInboxCategory;
  signals: AttentionSignal[];
  groupPriority: AttentionPriority;
  title: string;
  closureBlocked?: boolean;
};

export type AttentionDisplayNode = AttentionDisplaySignal | AttentionDisplayGroup;

export type AttentionInboxResult = {
  signals: AttentionSignal[];
  byCategory: Record<AttentionInboxCategory, AttentionSignal[]>;
  displayByCategory: Record<AttentionInboxCategory, AttentionDisplayNode[]>;
  closureBlockedEntities: Set<string>;
  counts: {
    total: number;
    p0: number;
    documents: number;
    finance: number;
    operations: number;
    closure: number;
  };
};

const PRIORITY_RANK: Record<AttentionPriority, number> = {
  P0: 0,
  P1: 1,
  P2: 2,
  P3: 3,
};

const SLA_CRITICAL_OVERDUE_DAYS = 7;

export const ATTENTION_INBOX_CATEGORY_LABELS: Record<AttentionInboxCategory, string> = {
  documents: 'Документы',
  finance: 'Финансы',
  operations: 'Задачи',
  closure: 'Закрытие',
};

/** Lowercase plural for «Все {label} (N) →» navigation links */
export const ATTENTION_INBOX_CATEGORY_ALL_LABELS: Record<AttentionInboxCategory, string> = {
  documents: 'документы',
  finance: 'финансы',
  operations: 'задачи',
  closure: 'закрытие',
};

export const ATTENTION_INBOX_CATEGORY_ORDER: AttentionInboxCategory[] = [
  'documents',
  'finance',
  'operations',
  'closure',
];

const GROUP_PROBLEM_LABEL: Record<AttentionInboxCategory, string> = {
  documents: 'проблем с документами',
  finance: 'финансовых действий',
  operations: 'задач',
  closure: 'закрытие',
};

function normalizeRole(role: string | null | undefined): string {
  return String(role || '').trim().toLowerCase();
}

function isFinanceRole(role: string): boolean {
  return role === 'accountant' || role === 'finance';
}

function isLogistLike(role: string): boolean {
  return role === 'logist' || role === 'dispatcher';
}

export function entityKey(entity: AttentionEntityRef): string {
  return `${entity.kind}:${entity.id}`;
}

function taskEntity(task: WorkspaceTask): AttentionEntityRef | null {
  if (task.routeId != null) return { kind: 'route', id: task.routeId };
  if (task.applicationId != null) return { kind: 'application', id: task.applicationId };
  return null;
}

export function overdueDaysPast(task: WorkspaceTask): number {
  if (!task.dueDate) return 0;
  const d = dayjs(task.dueDate).startOf('day');
  if (!d.isValid()) return 0;
  return Math.max(0, dayjs().startOf('day').diff(d, 'day'));
}

function taskRegistrySeverity(task: WorkspaceTask): string {
  return String(TASK_REGISTRY[String(task.taskType || '').toUpperCase()]?.severity || '').toLowerCase();
}

function taskTypeUpper(task: WorkspaceTask): string {
  return String(task.taskType || '').toUpperCase();
}

function isFinancePaymentTask(task: WorkspaceTask): boolean {
  const tt = taskTypeUpper(task);
  return tt.includes('PAYMENT') || tt === 'CLIENT_PAYMENT_DUE' || tt === 'PAYMENT_EXPECTED';
}

export function computeAttentionPriority(
  task: WorkspaceTask | null,
  options?: { isClosure?: boolean; financialComplete?: boolean }
): AttentionPriority {
  if (options?.isClosure) {
    if (options.financialComplete === false) return 'P1';
    return 'P2';
  }

  if (!task) return 'P3';

  const overdue = workspaceTaskIsOverdue(task);
  const days = overdueDaysPast(task);
  const tt = taskTypeUpper(task);

  if (overdue) {
    if (
      taskRegistrySeverity(task) === 'critical' ||
      days >= SLA_CRITICAL_OVERDUE_DAYS ||
      (isFinanceWorkspaceTask(task) && isFinancePaymentTask(task))
    ) {
      return 'P0';
    }
    if (days >= 1) return 'P1';
    if (tt === 'CARRIER_INVOICE_MISSING' || tt === 'INVOICE_EXPECTED') return 'P1';
    return 'P2';
  }

  if (task.dueDate) {
    const due = dayjs(task.dueDate).startOf('day');
    if (due.isValid()) {
      const diff = due.diff(dayjs().startOf('day'), 'day');
      if (diff >= 0 && diff <= 1) return 'P2';
    }
  }

  return 'P3';
}

function taskVisibleForRole(task: WorkspaceTask, roleNorm: string): boolean {
  if (isLogistLike(roleNorm) && isFinanceWorkspaceTask(task)) return false;
  if (isFinanceRole(roleNorm)) {
    const sem = getWorkspaceTaskSemantics(task);
    if (!isFinanceWorkspaceTask(task) && !sem.documentRelevant) return false;
  }
  return true;
}

function taskTitle(task: WorkspaceTask): string {
  const tt = taskTypeUpper(task);
  const label = TASK_REGISTRY[tt]?.label;
  if (label) return label;
  return tt || 'Задача';
}

function entityLabel(
  entity: AttentionEntityRef,
  routes: WorkspaceRoute[],
  applications: WorkspaceApplication[]
): string {
  if (entity.kind === 'route') {
    const route = routes.find((r) => r.id === entity.id);
    const dir =
      route?.direction ||
      (route?.loadingCity || route?.unloadingCity
        ? `${route?.loadingCity ?? '—'} → ${route?.unloadingCity ?? '—'}`
        : null);
    return dir ? `Маршрут #${entity.id} · ${dir}` : `Маршрут #${entity.id}`;
  }
  const app = applications.find((a) => a.id === entity.id);
  const num = app?.internalNumber?.trim() || app?.number?.trim() || String(entity.id);
  return `Заявка №${num}`;
}

export function compareAttentionSignals(a: AttentionSignal, b: AttentionSignal): number {
  const pr = PRIORITY_RANK[a.priority] - PRIORITY_RANK[b.priority];
  if (pr !== 0) return pr;
  const aOverdue = a.task && workspaceTaskIsOverdue(a.task) ? 0 : 1;
  const bOverdue = b.task && workspaceTaskIsOverdue(b.task) ? 0 : 1;
  if (aOverdue !== bOverdue) return aOverdue - bOverdue;
  const aDue = a.dueDate ? dayjs(a.dueDate).valueOf() : Number.POSITIVE_INFINITY;
  const bDue = b.dueDate ? dayjs(b.dueDate).valueOf() : Number.POSITIVE_INFINITY;
  if (aDue !== bDue) return aDue - bDue;
  return a.id.localeCompare(b.id);
}

function highestPriority(signals: AttentionSignal[]): AttentionPriority {
  if (signals.length === 0) return 'P3';
  return signals.reduce(
    (best, s) => (PRIORITY_RANK[s.priority] < PRIORITY_RANK[best] ? s.priority : best),
    signals[0].priority
  );
}

function buildTaskSignals(
  tasks: WorkspaceTask[],
  routes: WorkspaceRoute[],
  applications: WorkspaceApplication[],
  roleNorm: string
): AttentionSignal[] {
  const bySignalKey = new Map<string, AttentionSignal>();

  for (const task of tasks) {
    if (!taskVisibleForRole(task, roleNorm)) continue;
    if (!isOpenWorkspaceTask(task)) continue;
    const entity = taskEntity(task);
    if (!entity) continue;

    const inboxCat = resolveInboxCategory(task);
    const signalKey = `task:${task.id}`;

    const signal: AttentionSignal = {
      id: signalKey,
      category: inboxCat,
      priority: computeAttentionPriority(task),
      title: taskTitle(task),
      entityLabel: entityLabel(entity, routes, applications),
      entity,
      task,
      dueDate: task.dueDate ?? null,
      overdueDays: workspaceTaskIsOverdue(task) ? overdueDaysPast(task) : undefined,
      source: 'task',
    };

    bySignalKey.set(signalKey, signal);
  }

  return Array.from(bySignalKey.values());
}

function collectClosureEntities(
  tasks: WorkspaceTask[],
  routes: WorkspaceRoute[],
  applications: WorkspaceApplication[],
  roleNorm: string
): Map<string, { entity: AttentionEntityRef; title: string; financialComplete: boolean; task: WorkspaceTask | null }> {
  const out = new Map<
    string,
    { entity: AttentionEntityRef; title: string; financialComplete: boolean; task: WorkspaceTask | null }
  >();

  const derived = deriveReactiveWorkflowProblems({
    allTasks: tasks,
    routes,
    applications,
    roleName: roleNorm,
  });

  for (const problem of derived) {
    if (problem.problemType !== 'closure_blocker' || !problem.context) continue;
    const key = entityKey(problem.context);
    const proj =
      problem.context.kind === 'route'
        ? routes.find((r) => r.id === problem.context!.id)?.completionProjection
        : applications.find((a) => a.id === problem.context!.id)?.completionProjection;
    out.set(key, {
      entity: problem.context,
      title: problem.title,
      financialComplete: Boolean((proj as { financialComplete?: boolean } | null)?.financialComplete),
      task: problem.task ?? null,
    });
  }

  const ensure = (
    entity: AttentionEntityRef,
    projection: unknown,
    title: string
  ) => {
    if (!requiresClosureProjection(projection)) return;
    const key = entityKey(entity);
    if (out.has(key)) return;
    out.set(key, {
      entity,
      title,
      financialComplete: Boolean((projection as { financialComplete?: boolean } | null)?.financialComplete),
      task: null,
    });
  };

  for (const route of routes) {
    ensure({ kind: 'route', id: route.id }, route.completionProjection, `Маршрут #${route.id}: требуется закрытие`);
  }
  for (const app of applications) {
    const num = app.internalNumber?.trim() || app.number?.trim() || String(app.id);
    ensure(
      { kind: 'application', id: app.id },
      app.completionProjection,
      `Заявка №${num}: требуется закрытие`
    );
  }

  return out;
}

function buildClosureSignals(
  closureEntities: Map<string, { entity: AttentionEntityRef; title: string; financialComplete: boolean; task: WorkspaceTask | null }>,
  entitiesWithTaskSignals: Set<string>,
  routes: WorkspaceRoute[],
  applications: WorkspaceApplication[]
): AttentionSignal[] {
  const signals: AttentionSignal[] = [];

  for (const [key, info] of Array.from(closureEntities.entries())) {
    if (entitiesWithTaskSignals.has(key)) continue;

    const signal: AttentionSignal = {
      id: `closure:${key}`,
      category: 'closure',
      priority: computeAttentionPriority(null, {
        isClosure: true,
        financialComplete: info.financialComplete,
      }),
      title: 'Закрытие не завершено',
      entityLabel: entityLabel(info.entity, routes, applications),
      entity: info.entity,
      task: info.task,
      dueDate: info.task?.dueDate ?? null,
      source: 'closure',
    };
    signals.push(signal);
  }

  return signals;
}

export function buildCategoryDisplayNodes(
  signals: AttentionSignal[],
  closureBlockedEntities: Set<string>
): AttentionDisplayNode[] {
  const byEntity = new Map<string, AttentionSignal[]>();

  for (const signal of signals) {
    const key = entityKey(signal.entity);
    const list = byEntity.get(key) ?? [];
    list.push(signal);
    byEntity.set(key, list);
  }

  const nodes: AttentionDisplayNode[] = [];

  for (const [, entitySignals] of Array.from(byEntity.entries())) {
    const sorted = [...entitySignals].sort(compareAttentionSignals);
    const key = entityKey(sorted[0].entity);
    const blocked = closureBlockedEntities.has(key);
    const p0Signals = sorted.filter((s) => s.priority === 'P0');

    if (sorted.length < HYBRID_GROUP_THRESHOLD) {
      for (const signal of sorted) {
        nodes.push({ kind: 'signal', signal, closureBlocked: blocked });
      }
      continue;
    }

    for (const signal of p0Signals) {
      nodes.push({ kind: 'signal', signal, closureBlocked: blocked });
    }

    const collapsed = sorted.filter((s) => s.priority !== 'P0');
    if (collapsed.length > 0) {
      const category = collapsed[0].category;
      nodes.push({
        kind: 'group',
        id: `group-${key}-${category}`,
        entity: sorted[0].entity,
        entityLabel: sorted[0].entityLabel,
        category,
        signals: collapsed,
        groupPriority: highestPriority(collapsed),
        title: `${collapsed.length} ${GROUP_PROBLEM_LABEL[category]}`,
        closureBlocked: blocked,
      });
    }
  }

  nodes.sort((a, b) => {
    const sigA = a.kind === 'signal' ? a.signal : a.signals[0];
    const sigB = b.kind === 'signal' ? b.signal : b.signals[0];
    return compareAttentionSignals(sigA, sigB);
  });

  return nodes;
}

function groupSignalsByCategory(signals: AttentionSignal[]): Record<AttentionInboxCategory, AttentionSignal[]> {
  const buckets: Record<AttentionInboxCategory, AttentionSignal[]> = {
    documents: [],
    finance: [],
    operations: [],
    closure: [],
  };
  for (const signal of signals) {
    buckets[signal.category].push(signal);
  }
  for (const cat of ATTENTION_INBOX_CATEGORY_ORDER) {
    buckets[cat].sort(compareAttentionSignals);
  }
  return buckets;
}

export function buildAttentionInbox(params: {
  allTasks: WorkspaceTask[];
  routes: WorkspaceRoute[];
  applications: WorkspaceApplication[];
  roleName: string | null | undefined;
}): AttentionInboxResult {
  const roleNorm = normalizeRole(params.roleName);
  const taskSignals = buildTaskSignals(params.allTasks, params.routes, params.applications, roleNorm);

  const entitiesWithTaskSignals = new Set(taskSignals.map((s) => entityKey(s.entity)));

  const closureEntities = collectClosureEntities(
    params.allTasks,
    params.routes,
    params.applications,
    roleNorm
  );

  const closureBlockedEntities = new Set<string>();
  for (const key of Array.from(closureEntities.keys())) {
    if (entitiesWithTaskSignals.has(key)) {
      closureBlockedEntities.add(key);
    }
  }

  const closureSignals = buildClosureSignals(
    closureEntities,
    entitiesWithTaskSignals,
    params.routes,
    params.applications
  );

  const signals = [...taskSignals, ...closureSignals];
  const byCategory = groupSignalsByCategory(signals);

  const displayByCategory = {} as Record<AttentionInboxCategory, AttentionDisplayNode[]>;
  for (const cat of ATTENTION_INBOX_CATEGORY_ORDER) {
    displayByCategory[cat] =
      cat === 'closure'
        ? byCategory.closure.map((signal) => ({ kind: 'signal' as const, signal }))
        : buildCategoryDisplayNodes(byCategory[cat], closureBlockedEntities);
  }

  const p0 = signals.filter((s) => s.priority === 'P0').length;

  return {
    signals,
    byCategory,
    displayByCategory,
    closureBlockedEntities,
    counts: {
      total: signals.length,
      p0,
      documents: byCategory.documents.length,
      finance: byCategory.finance.length,
      operations: byCategory.operations.length,
      closure: byCategory.closure.length,
    },
  };
}

export function buildAttentionSignalSearchText(
  signal: AttentionSignal,
  routes: WorkspaceRoute[],
  applications: WorkspaceApplication[]
): string {
  const parts: string[] = [signal.entityLabel, signal.title, String(signal.entity.id)];

  if (signal.task?.carrierName) parts.push(signal.task.carrierName);
  if (signal.task?.applicationClientName) parts.push(signal.task.applicationClientName);
  if (signal.task?.applicationNumber) parts.push(signal.task.applicationNumber);
  if (signal.task?.applicationExternalNumber) parts.push(signal.task.applicationExternalNumber);

  if (signal.entity.kind === 'route') {
    const route = routes.find((r) => r.id === signal.entity.id);
    if (route?.number) parts.push(route.number);
    if (route?.client?.name) parts.push(route.client.name);
    if (route?.client?.shortName) parts.push(route.client.shortName);
    if (route?.carrier?.name) parts.push(route.carrier.name);
    for (const link of route?.applications ?? []) {
      const clientName = link.application?.client?.name ?? link.application?.client?.shortName;
      if (clientName) parts.push(clientName);
    }
  } else {
    const app = applications.find((a) => a.id === signal.entity.id);
    if (app?.number) parts.push(app.number);
    if (app?.internalNumber) parts.push(app.internalNumber);
    if (app?.externalNumber) parts.push(app.externalNumber);
    if (app?.client?.name) parts.push(app.client.name);
    if (app?.client?.shortName) parts.push(app.client.shortName);
  }

  return parts
    .filter((p) => p != null && String(p).trim() !== '')
    .join(' ')
    .toLowerCase();
}

export function filterAttentionSignalsByQuery(
  signals: AttentionSignal[],
  query: string,
  routes: WorkspaceRoute[],
  applications: WorkspaceApplication[]
): AttentionSignal[] {
  const q = query.trim().toLowerCase();
  if (!q) return signals;
  return signals.filter((signal) =>
    buildAttentionSignalSearchText(signal, routes, applications).includes(q)
  );
}

export type AttentionInboxPersistState = {
  expanded: boolean;
  groupsExpanded: Record<string, boolean>;
};

export function readAttentionInboxPersistState(): AttentionInboxPersistState {
  if (typeof window === 'undefined') {
    return { expanded: false, groupsExpanded: {} };
  }
  try {
    const raw = localStorage.getItem(WORKSPACE_ATTENTION_INBOX_STORAGE_KEY);
    if (!raw) return { expanded: false, groupsExpanded: {} };
    const parsed = JSON.parse(raw) as Partial<AttentionInboxPersistState & { categoriesExpanded?: unknown }>;
    return {
      expanded: Boolean(parsed.expanded),
      groupsExpanded: parsed.groupsExpanded ?? {},
    };
  } catch {
    return { expanded: false, groupsExpanded: {} };
  }
}

export function writeAttentionInboxPersistState(state: AttentionInboxPersistState): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(WORKSPACE_ATTENTION_INBOX_STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* ignore quota errors */
  }
}
